// src/components/portal/PortalSearch.js — Global search in the partner
// portal (Travis, 10/21). It searches only the partner's own universe
// (GET /api/portal/search, portalController.js): mandates they can see,
// their contacts and firms, their submitted names, and documents on their engaged
// mandates. Same keys as the CRM's UniversalSearch: arrows move, Enter
// opens, Esc closes, "/" or Cmd/Ctrl+K focuses.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import api from '../../lib/api'

const GROUPS = [
  { key: 'mandates',  label: 'Mandates',        to: r => `/portal/mandates/${r.id}` },
  { key: 'contacts',  label: 'Contacts',        to: r => r.contact_id ? `/portal/contacts/${r.contact_id}` : r.firm_id ? `/portal/firms/${r.firm_id}` : '/portal/contacts' },
  { key: 'firms',     label: 'Firms',           to: r => `/portal/firms/${r.id}` },
  { key: 'names',     label: 'Submitted names', to: r => `/portal/mandates/${r.mandate_id}/add-names` },
  { key: 'documents', label: 'Documents',       to: r => `/portal/mandates/${r.mandate_id}` },
]

export default function PortalSearch() {
  const navigate = useNavigate()
  const inputRef = useRef(null)
  const boxRef   = useRef(null)
  const [text, setText]     = useState('')
  const [term, setTerm]     = useState('')
  const [open, setOpen]     = useState(false)
  const [active, setActive] = useState(0)

  useEffect(() => {
    const t = setTimeout(() => setTerm(text.trim()), 200)
    return () => clearTimeout(t)
  }, [text])

  const { data, isFetching } = useQuery({
    queryKey: ['portal-search', term],
    queryFn: () => api.get('/api/portal/search', { params: { q: term } }).then(r => r.data.data),
    enabled: term.length >= 2,
    staleTime: 30 * 1000,
    placeholderData: prev => prev,
  })

  const items = useMemo(() => {
    if (!data || term.length < 2) return []
    return GROUPS.flatMap(g => (data[g.key]?.results ?? []).map(r => ({ ...r, group: g })))
  }, [data, term])

  useEffect(() => { setActive(0) }, [items])

  useEffect(() => {
    function onKey(e) {
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName) ||
        document.activeElement?.isContentEditable
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing)) {
        e.preventDefault()
        inputRef.current?.focus()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    function onDown(e) { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  function go(item) {
    setOpen(false)
    navigate(item.group.to(item))
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(a + 1, items.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter' && items[active]) { e.preventDefault(); go(items[active]) }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur() }
  }

  const showPanel = open && term.length >= 2
  let index = -1

  return (
    <div ref={boxRef} style={{ position: 'relative', width: '100%', maxWidth: 440 }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
        style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}>
        <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        ref={inputRef}
        className="input"
        type="search"
        placeholder="Search your mandates, contacts and documents"
        aria-label="Search the portal"
        value={text}
        onChange={e => { setText(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        style={{ width: '100%', paddingLeft: 34, paddingRight: 44 }}
      />
      <span style={{
        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
        fontSize: 11, color: 'var(--text-muted)', border: '1px solid var(--border)',
        borderRadius: 4, padding: '1px 5px', pointerEvents: 'none',
      }}>⌘K</span>

      {showPanel && (
        <div role="listbox" style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 50,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.2)', maxHeight: 460, overflowY: 'auto', padding: '6px 0',
        }}>
          {items.length === 0 && (
            <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-muted)' }}>
              {isFetching ? 'Searching…' : `No matches for "${term}"`}
            </div>
          )}
          {GROUPS.map(g => {
            const rows = data?.[g.key]?.results ?? []
            if (!rows.length) return null
            return (
              <div key={g.key}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', padding: '8px 14px 4px' }}>
                  {g.label}
                </div>
                {rows.map(r => {
                  index += 1
                  const i = index
                  return (
                    <div
                      key={r.id}
                      role="option"
                      aria-selected={i === active}
                      onMouseEnter={() => setActive(i)}
                      onMouseDown={e => { e.preventDefault(); go({ ...r, group: g }) }}
                      style={{
                        padding: '7px 14px', cursor: 'pointer',
                        background: i === active ? 'var(--hover-tint, rgba(255,255,255,0.05))' : 'transparent',
                        display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0,
                      }}
                    >
                      <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500, whiteSpace: 'nowrap' }}>{r.name}</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        {r.detail && (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.detail}
                          </div>
                        )}
                        {r.match?.length > 0 && r.match[0] !== 'Name' && r.match[0] !== 'File' && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <span style={{ fontWeight: 600 }}>Match:</span> {r.match.join(' · ')}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
